import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pos, setPOs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(1);
  const limit = 25;

  useEffect(() => { loadPOs(); }, [page, statusFilter]);

  const loadPOs = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await contractsAPI.listPurchaseOrders(params);
      setPOs(res.data.purchase_orders || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load purchase orders'); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); loadPOs(); };
  const totalPages = Math.ceil(total / limit);

  const STATUS_COLORS = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    awaited: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-slate-50 text-slate-600 border-slate-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Purchase Orders</h1>
          <p className="text-xs text-muted-foreground">{total} sale orders from customers</p>
        </div>
        <Button size="sm" onClick={() => navigate('/contracts/purchase-orders/new')} data-testid="new-po-btn">
          <Plus className="w-4 h-4 mr-1" /> Register PO
        </Button>
      </div>

      <Card>
        <CardContent className="p-3 flex gap-2 flex-wrap">
          <div className="flex gap-1 flex-1 min-w-[200px]">
            <Input placeholder="Search PO, customer..." value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="h-8 text-xs" data-testid="po-search" />
            <Button size="sm" variant="outline" className="h-8" onClick={handleSearch}><Search className="w-3.5 h-3.5" /></Button>
          </div>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="po-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="awaited">Awaited</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="po-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">PO Number</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Customer</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Date</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Project</th>
                <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Value</th>
                <th className="text-center p-2.5 text-xs font-medium text-muted-foreground">Items</th>
                <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
              ) : pos.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No purchase orders found</td></tr>
              ) : pos.map(po => (
                <tr key={po.id} className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/contracts/purchase-orders/${po.id}`)}
                  data-testid={`po-row-${po.po_number}`}>
                  <td className="p-2.5 font-mono text-xs font-medium">{po.po_number}</td>
                  <td className="p-2.5 text-xs">{po.customer_name}</td>
                  <td className="p-2.5 text-xs font-mono text-muted-foreground">{po.po_date}</td>
                  <td className="p-2.5 text-xs text-muted-foreground">{po.project_number || '-'}</td>
                  <td className="p-2.5 text-xs text-right font-medium">{po.value ? `Rs ${po.value.toLocaleString()}` : '-'}</td>
                  <td className="p-2.5 text-xs text-center">{po.items?.length || 0}</td>
                  <td className="p-2.5">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[po.status] || ''}`}>
                      {po.status?.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                <ChevronLeft className="w-3 h-3 mr-1" /> Prev
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                Next <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
